'use client';

import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Generic React Error Boundary for wrapping component subtrees.
 *
 * Next.js App Router provides automatic error boundaries via error.tsx files,
 * but this component is useful for isolating errors within a page so that a
 * single broken widget doesn't take down the entire route.
 *
 * Usage:
 *   <ErrorBoundary fallback={<p>Widget failed</p>}>
 *     <SomeRiskyComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // NOTE: CLAUDE.md says no console.log/warn, but console.error is the only
    // viable option inside a class component error boundary (no hooks, no async).
    // The backend Pino Logger is not available on the client side.
    //
    // Tag the log with [REACT_HUNT] + serialise the componentStack into the
    // message so it survives Next.js error.tsx swallowing the second arg.
    // Also persist into sessionStorage so we can read it AFTER the page
    // remounts into the ErrorState fallback (componentDidCatch fires before
    // Next.js GlobalError replaces the tree, so the second arg is otherwise
    // gone by the time the user opens DevTools).
    const stack = info?.componentStack || '(no componentStack)';
    const blob = `[REACT_HUNT] ${error?.name}: ${error?.message}\n--- componentStack ---${stack}`;
    console.error(blob, error, info);
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('__lastReactErrorStack', blob.slice(0, 8000));
        sessionStorage.setItem('__lastReactErrorAt', String(Date.now()));
      }
    } catch {}

    // Best-effort: forward to /api/csp-report (Pino-logged sink) so the
    // message + componentStack lands in Railway logs without us depending
    // on the user opening DevTools.
    try {
      const url = typeof window !== 'undefined' ? window.location.pathname : 'ssr';
      fetch('/api/csp-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'ErrorBoundary',
          url,
          message: error?.message || String(error),
          componentStack: stack.slice(0, 3000),
          stack: (error?.stack || '').slice(0, 3000),
          ts: Date.now(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // never throw from componentDidCatch
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md">
            An unexpected error occurred in this section. The rest of the app should still work.
          </p>
          <button
            onClick={this.handleReset}
            className="px-5 py-2.5 text-sm font-medium rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
